import { mapbox } from "../../mapbox.js";
import { mapboxAccessToken } from "../../access_tokens";
import circle from "@turf/circle";

export const getElevations = async (coordinatePath, arrayStep = 10) => {
  const elevationCoordinates = coordinatePath.filter((element, index) => {
    return index % arrayStep === 0;
  });

  const responses = await Promise.all(
    elevationCoordinates.map(([lng, lat]) =>
      fetch(
        `https://api.mapbox.com/v4/mapbox.mapbox-terrain-v2/tilequery/${lng},${lat}.json?&access_token=${mapboxAccessToken}`
      ).then((response) => response.json())
    )
  );

  const data = responses
    .map((res) => res.features.slice(-1)[0].properties.ele)
    .map((d, i, n) => {
      if (d) {
        return d;
      } else if (i === 0) {
        return n[i + 1];
      } else {
        return n[i - 1];
      }
    });

  return data;
};

export const getElevationsMapQuery = (coordinatePath, map, arrayStep = 10) => {
  const elevationCoordinates = coordinatePath.filter((element, index) => {
    return index % arrayStep === 0;
  });

  return elevationCoordinates.map((d) => map.queryTerrainElevation(d, { exaggerated: true }));
};

export const addFeatureExtrusions = ({
  map,
  formatterFunction,
  featureSet,
  layerID,
  color,
  markerRadius,
  markerHeight = 50,
}) => {
  if (featureSet === null) {
    return;
  }

  if (map.getLayer(layerID)) {
    map.removeLayer(layerID);
  }

  if (map.getSource(layerID)) {
    map.removeSource(layerID);
  }

  featureSet.features.forEach((d) => {
    d.properties.original_center = d.geometry.coordinates;
    d.geometry = circle(d.geometry.coordinates, markerRadius, {
      steps: 30,
    }).geometry;
  });

  map.addSource(layerID, {
    type: "geojson",
    data: featureSet,
  });

  map.addLayer({
    id: layerID,
    source: layerID,
    type: "fill-extrusion",
    paint: {
      "fill-extrusion-color": color,
      "fill-extrusion-opacity": 0.8,
      "fill-extrusion-height": markerHeight,
      "fill-extrusion-base": 0,
    },
  });

  map.on("click", layerID, (e) => {
    if (
      (layerID === "wqp-points" || layerID === "wade-points" || layerID === "ca-gage-points") &&
      map.queryRenderedFeatures(e.point).some((feature) => feature.source === "nwis-points")
    ) {
      return;
    }

    if (e.features.length > 0) {
      new mapbox.Popup({
        closeButton: false,
        closeOnClick: true,
        maxWidth: 400,
      })
        .setLngLat(e.lngLat)
        .setHTML(formatterFunction({ feature: e.features[0] }))
        .addTo(map);
    }
  });

  return;
};

export const addTopoLayer = ({ map }) => {
  map.addSource("mapbox-dem", {
    type: "raster-dem",
    url: "mapbox://mapbox.mapbox-terrain-dem-v1",
    tileSize: 512,
    maxzoom: 14,
  });

  // add the DEM source as a terrain layer with exaggerated height
  map.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });

  map.addLayer({
    id: "sky",
    type: "sky",
    paint: {
      "sky-type": "atmosphere",
      "sky-atmosphere-sun": [0.0, 0.0],
      "sky-atmosphere-sun-intensity": 15,
    },
  });
};

export const addBuildingLayer = ({ map }) => {
  // reference: https://docs.mapbox.com/mapbox-gl-js/example/3d-buildings/

  // Insert the layer beneath any symbol layer.
  const layers = map.getStyle().layers;
  const labelLayerId = layers.find(
    (layer) => layer.type === "symbol" && layer.layout["text-field"]
  ).id;

  // The 'building' layer in the Mapbox Streets
  // vector tileset contains building height data
  // from OpenStreetMap.
  map.addLayer(
    {
      id: "add-3d-buildings",
      source: "composite",
      "source-layer": "building",
      filter: ["==", "extrude", "true"],
      type: "fill-extrusion",
      minzoom: 15,
      paint: {
        "fill-extrusion-color": "#aaa",

        // Use an 'interpolate' expression to
        // add a smooth transition effect to
        // the buildings as the user zooms in.
        "fill-extrusion-height": [
          "interpolate",
          ["linear"],
          ["zoom"],
          12,
          0,
          12.05,
          ["get", "height"],
        ],
        "fill-extrusion-base": [
          "interpolate",
          ["linear"],
          ["zoom"],
          12,
          0,
          12.05,
          ["get", "min_height"],
        ],
        "fill-extrusion-opacity": 0.6,
      },
    },
    labelLayerId
  );
};

export const clearRiverLines = ({ map, sourceID = "route" }) => {
  if (map.getLayer(sourceID)) {
    map.removeLayer(sourceID);
  }

  if (map.getSource(sourceID)) {
    map.removeSource(sourceID);
  }
};

export const drawFlowPath = ({
  map,
  featureData,
  sourceID = "route",
  casingColor = '#0A2472',
  waterColor = '#00BFFF',
  casingWidth = 8,
  waterWidth = 5,
}) => {
  const casingID = sourceID + '-casing';
  const waterID = sourceID + '-water';

  // Clear any existing layers
  clearRiverLines({ map, sourceID: casingID });
  clearRiverLines({ map, sourceID: waterID });

  // Add casing layer first (bottom) - dark outline for contrast
  addRiverLayer({
    map,
    featureData,
    sourceID: casingID,
    lineColor: casingColor,
    lineWidth: casingWidth,
    opacity: 0.8,
    blur: 1,
  });

  // Add water layer on top - bright visible color
  addRiverLayer({
    map,
    featureData,
    sourceID: waterID,
    lineColor: waterColor,
    lineWidth: waterWidth,
    opacity: 1.0,
    blur: 0,
  });
};

// Helper for adding individual river layers with full styling control
const addRiverLayer = ({
  map,
  featureData,
  sourceID,
  lineColor,
  lineWidth,
  opacity = 1,
  blur = 0,
}) => {
  const features = featureData.map((river) => {
    const featureType = Array.isArray(river.geometry.coordinates[0][0])
      ? "MultiLineString"
      : "LineString";
    return {
      type: "Feature",
      geometry: {
        type: featureType,
        coordinates: river.geometry.coordinates,
      },
    };
  });

  map.addSource(sourceID, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features,
    },
  });

  map.addLayer({
    id: sourceID,
    type: "line",
    source: sourceID,
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": lineColor,
      "line-width": lineWidth,
      "line-opacity": opacity,
      "line-blur": blur,
    },
  });
};

// Hide ALL internal administrative boundaries from Mapbox base style permanently
// We'll use our own custom layers instead
export const hideEthiopiaAdminBoundaries = ({ map }) => {
  const style = map.getStyle();
  if (!style || !style.layers) return;

  // Hide all admin boundary layers except admin-0 (country borders)
  style.layers.forEach(layer => {
    const layerId = layer.id.toLowerCase();

    // Check if this is an internal admin boundary layer (not country-level)
    const isInternalAdmin = (
      // Admin-1, admin-2, etc. but not admin-0
      (layerId.includes('admin') && !layerId.includes('admin-0') && !layerId.includes('admin0')) ||
      // State/province/region boundaries
      layerId.includes('state-boundary') ||
      layerId.includes('state-border') ||
      layerId.includes('province') ||
      layerId.includes('region-bound') ||
      // Generic internal boundaries
      (layerId.includes('admin-1') || layerId.includes('admin-2') ||
       layerId.includes('admin1') || layerId.includes('admin2'))
    );

    // Don't hide country-level borders
    const isCountryLevel = layerId.includes('admin-0') ||
                           layerId.includes('admin0') ||
                           layerId.includes('country');

    if (isInternalAdmin && !isCountryLevel) {
      try {
        map.setLayoutProperty(layer.id, 'visibility', 'none');
      } catch (e) {
        // Silently ignore
      }
    }
  });
};

// Add custom Ethiopia regional (ethnic federalist) boundaries as a raster image layer
export const addEthiopiaRegionalBoundaries = ({ map }) => {
  const sourceId = 'ethiopia-regions-source';
  const layerId = 'ethiopia-regions-layer';

  // Check if already added
  if (map.getSource(sourceId)) return;

  // Image coordinates from QGIS: [top-left, top-right, bottom-right, bottom-left]
  // Format: [[lng, lat], [lng, lat], [lng, lat], [lng, lat]]
  // Bounds: 32.6779435804377840,2.8938753453555766 : 48.2544379281564346,15.3046336733863164
  const imageCoordinates = [
    [32.6779435804377840, 15.3046336733863164],  // top-left (NW)
    [48.2544379281564346, 15.3046336733863164],  // top-right (NE)
    [48.2544379281564346, 2.8938753453555766],   // bottom-right (SE)
    [32.6779435804377840, 2.8938753453555766]    // bottom-left (SW)
  ];

  map.addSource(sourceId, {
    type: 'image',
    url: 'ethnic-federalist-boundaries.png',
    coordinates: imageCoordinates
  });

  map.addLayer({
    id: layerId,
    type: 'raster',
    source: sourceId,
    paint: {
      'raster-opacity': 0.85,
      'raster-saturation': -0.3,
      'raster-contrast': 0.1
    },
    layout: {
      'visibility': 'none'  // Initially hidden
    }
  });
};

// Toggle visibility of Ethiopia regional boundaries (for Ethnic/2026 mode)
export const toggleEthiopiaRegionalBoundaries = ({ map, visible }) => {
  const layerId = 'ethiopia-regions-layer';
  const labelsLayerId = 'ethnic-regions-labels';

  if (map.getLayer(layerId)) {
    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  }
  if (map.getLayer(labelsLayerId)) {
    map.setLayoutProperty(labelsLayerId, 'visibility', visible ? 'visible' : 'none');
  }
};

// Helper function to get responsive text size based on viewport
const getResponsiveLabelSize = () => {
  const width = window.innerWidth;
  if (width <= 700) return { textSize: 9, haloWidth: 1 };        // Mobile
  if (width <= 1024) return { textSize: 11, haloWidth: 1.2 };    // Tablet
  return { textSize: 13, haloWidth: 1.5 };                        // Desktop
};

// Add labels for ethnic/regional map (2026 mode)
// Excludes micro-states: Harari, Addis Ababa, Dire Dawa
export const addEthnicRegionLabels = ({ map }) => {
  const sourceId = 'ethnic-regions-labels-source';
  const layerId = 'ethnic-regions-labels';

  // Check if already added
  if (map.getSource(sourceId)) return;

  const { textSize, haloWidth } = getResponsiveLabelSize();

  // Major regions with approximate center coordinates [lng, lat]
  // SNNPR split into: South Ethiopia, South West Ethiopia, Central Ethiopia, Sidama
  const regions = [
    { name: 'Oromia', center: [39.5, 7.5] },
    { name: 'Amhara', center: [38.2, 11.3] },
    { name: 'Tigray', center: [39.0, 13.8] },
    { name: 'Somali', center: [44.0, 7.0] },
    { name: 'Afar', center: [41.0, 12.0] },
    { name: 'South\nEthiopia', center: [37.0, 5.6] },
    { name: 'South West\nEthiopia', center: [35.75, 6.75] },
    { name: 'Central\nEthiopia', center: [37.88, 7.75] },
    { name: 'Benishangul-\nGumuz', center: [35.5, 10.5] },
    { name: 'Gambela', center: [34.3, 8.0] },
    { name: 'Sidama', center: [38.5, 6.6] },
  ];

  const geojson = {
    type: 'FeatureCollection',
    features: regions.map(region => ({
      type: 'Feature',
      properties: { name: region.name },
      geometry: {
        type: 'Point',
        coordinates: region.center
      }
    }))
  };

  map.addSource(sourceId, {
    type: 'geojson',
    data: geojson
  });

  map.addLayer({
    id: layerId,
    type: 'symbol',
    source: sourceId,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': textSize,
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-transform': 'uppercase',
      'text-letter-spacing': 0.1,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'visibility': 'none'  // Initially hidden
    },
    paint: {
      'text-color': '#FFFFFF',
      'text-halo-color': 'rgba(0, 0, 0, 0.8)',
      'text-halo-width': haloWidth,
      'text-halo-blur': 0.5
    }
  });
};

// Add historical imperial province boundaries as a raster image layer
export const addImperialProvincesLayer = ({ map }) => {
  const sourceId = 'imperial-provinces-source';
  const layerId = 'imperial-provinces-layer';

  // Check if already added
  if (map.getSource(sourceId)) return;

  // Image coordinates: [top-left, top-right, bottom-right, bottom-left]
  // Format: [[lng, lat], [lng, lat], [lng, lat], [lng, lat]]
  const imageCoordinates = [
    [32.4148, 18.4270],  // top-left (NW)
    [48.0076, 18.4270],  // top-right (NE)
    [48.0076, 2.8921],   // bottom-right (SE)
    [32.4148, 2.8921]    // bottom-left (SW)
  ];

  map.addSource(sourceId, {
    type: 'image',
    url: 'ethiopia-provinces-white.png',
    coordinates: imageCoordinates
  });

  map.addLayer({
    id: layerId,
    type: 'raster',
    source: sourceId,
    paint: {
      'raster-opacity': 0.85,
      'raster-saturation': -0.3,
      'raster-contrast': 0.1
    },
    layout: {
      'visibility': 'none'  // Initially hidden
    }
  });
};

// Toggle visibility of the imperial provinces layer
export const toggleImperialProvincesLayer = ({ map, visible }) => {
  const layerId = 'imperial-provinces-layer';
  const labelsLayerId = 'imperial-provinces-labels';

  if (map.getLayer(layerId)) {
    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  }
  if (map.getLayer(labelsLayerId)) {
    map.setLayoutProperty(labelsLayerId, 'visibility', visible ? 'visible' : 'none');
  }
};

// Add province labels for the 1974 imperial map
export const addImperialProvinceLabels = ({ map, provinces }) => {
  const sourceId = 'imperial-provinces-labels-source';
  const layerId = 'imperial-provinces-labels';

  // Check if already added
  if (map.getSource(sourceId)) return;

  const { textSize, haloWidth } = getResponsiveLabelSize();

  // Create GeoJSON from province centers
  const geojson = {
    type: 'FeatureCollection',
    features: provinces.map(province => ({
      type: 'Feature',
      properties: {
        name: province.name,
        name_amharic: province.name_amharic
      },
      geometry: {
        type: 'Point',
        coordinates: province.center
      }
    }))
  };

  map.addSource(sourceId, {
    type: 'geojson',
    data: geojson
  });

  map.addLayer({
    id: layerId,
    type: 'symbol',
    source: sourceId,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': textSize,
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-transform': 'uppercase',
      'text-letter-spacing': 0.1,
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'visibility': 'none'  // Initially hidden
    },
    paint: {
      'text-color': '#FFFFFF',
      'text-halo-color': 'rgba(0, 0, 0, 0.8)',
      'text-halo-width': haloWidth,
      'text-halo-blur': 0.5
    }
  });
};

// Add satellite imagery layer for river run view
export const addSatelliteLayer = ({ map }) => {
  const sourceId = 'satellite-source';
  const layerId = 'satellite-layer';

  // Check if already added
  if (map.getSource(sourceId)) return;

  map.addSource(sourceId, {
    type: 'raster',
    url: 'mapbox://mapbox.satellite',
    tileSize: 256
  });

  // Add satellite layer at the bottom (index 0) so other layers render on top
  map.addLayer({
    id: layerId,
    type: 'raster',
    source: sourceId,
    paint: {
      'raster-opacity': 1
    }
  }, map.getStyle().layers[0]?.id);
};

// Remove satellite imagery layer
export const removeSatelliteLayer = ({ map }) => {
  const sourceId = 'satellite-source';
  const layerId = 'satellite-layer';

  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }
};

// Show city labels on the map
export const showCityLabels = ({ map }) => {
  const style = map.getStyle();
  if (!style || !style.layers) return;

  // Find and show place label layers
  style.layers.forEach(layer => {
    const layerId = layer.id.toLowerCase();

    // Match place/settlement/city label layers
    const isCityLabel = (
      layerId.includes('place-') ||
      layerId.includes('settlement') ||
      layerId.includes('place_label') ||
      layerId.includes('place-label') ||
      (layerId.includes('label') && layerId.includes('city')) ||
      (layerId.includes('label') && layerId.includes('town'))
    );

    // Exclude country labels and state labels
    const isNotWanted = (
      layerId.includes('country') ||
      layerId.includes('state') ||
      layerId.includes('continent')
    );

    if (isCityLabel && !isNotWanted) {
      try {
        map.setLayoutProperty(layer.id, 'visibility', 'visible');
      } catch (e) {
        // Silently ignore
      }
    }
  });
};

// Add custom Ethiopian city labels for better visibility
export const addEthiopianCityLabels = ({ map }) => {
  const sourceId = 'ethiopian-cities-source';
  const layerId = 'ethiopian-cities-labels';

  // Check if already added
  if (map.getSource(sourceId)) return;

  // Major Ethiopian cities with coordinates [lng, lat]
  const cities = [
    { name: 'Addis Ababa', center: [38.7578, 9.0054], rank: 1 },
    { name: 'Dire Dawa', center: [42.4500, 9.6000], rank: 2 },
    { name: 'Mekelle', center: [39.4700, 13.4967], rank: 2 },
    { name: 'Gondar', center: [37.4667, 12.6000], rank: 2 },
    { name: 'Bahir Dar', center: [37.3833, 11.5833], rank: 2 },
    { name: 'Hawassa', center: [38.4767, 7.0500], rank: 2 },
    { name: 'Adama', center: [39.2671, 8.5400], rank: 2 },
    { name: 'Jimma', center: [36.8333, 7.6667], rank: 3 },
    { name: 'Dessie', center: [39.6333, 11.1333], rank: 3 },
    { name: 'Harar', center: [42.1167, 9.3117], rank: 3 },
    { name: 'Jijiga', center: [42.7833, 9.3500], rank: 3 },
    { name: 'Debre Markos', center: [37.7333, 10.3500], rank: 3 },
    { name: 'Axum', center: [38.7167, 14.1167], rank: 3 },
    { name: 'Lalibela', center: [39.0333, 12.0333], rank: 3 },
    { name: 'Arba Minch', center: [37.5500, 6.0333], rank: 3 },
    { name: 'Assosa', center: [34.5333, 10.0667], rank: 3 },
    { name: 'Gambela', center: [34.5833, 8.2500], rank: 3 },
    { name: 'Semera', center: [41.0167, 11.7833], rank: 3 },
  ];

  const geojson = {
    type: 'FeatureCollection',
    features: cities.map(city => ({
      type: 'Feature',
      properties: {
        name: city.name,
        rank: city.rank
      },
      geometry: {
        type: 'Point',
        coordinates: city.center
      }
    }))
  };

  map.addSource(sourceId, {
    type: 'geojson',
    data: geojson
  });

  map.addLayer({
    id: layerId,
    type: 'symbol',
    source: sourceId,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': [
        'case',
        ['==', ['get', 'rank'], 1], 14,
        ['==', ['get', 'rank'], 2], 11,
        9
      ],
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      'text-anchor': 'center',
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'visibility': 'visible'
    },
    paint: {
      'text-color': '#1a1a1a',
      'text-halo-color': '#FFFFFF',
      'text-halo-width': 2,
      'text-halo-blur': 0.5
    }
  });
};

// Basin lookup cache
let basinsGeoJSON = null;

// Rivers lookup cache
let riversGeoJSON = null;

// Load Ethiopian rivers GeoJSON for proximity queries
export const loadEthiopianRivers = async () => {
  if (riversGeoJSON) return riversGeoJSON;

  try {
    const response = await fetch('/data/ethiopia_rivers.json');
    riversGeoJSON = await response.json();
    return riversGeoJSON;
  } catch (e) {
    console.error('Failed to load Ethiopian rivers:', e);
    return null;
  }
};

// Calculate distance from point to line segment
const pointToSegmentDistance = (point, segStart, segEnd) => {
  const [px, py] = point;
  const [x1, y1] = segStart;
  const [x2, y2] = segEnd;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    // Segment is a point
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  }

  // Project point onto line, clamped to segment
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const nearestX = x1 + t * dx;
  const nearestY = y1 + t * dy;

  return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
};

// Calculate minimum distance from point to a LineString
const pointToLineDistance = (point, lineCoords) => {
  let minDist = Infinity;

  for (let i = 0; i < lineCoords.length - 1; i++) {
    const dist = pointToSegmentDistance(point, lineCoords[i], lineCoords[i + 1]);
    if (dist < minDist) minDist = dist;
  }

  return minDist;
};

// Find nearest named river to a point (within threshold in degrees, ~0.1 deg ≈ 11km)
export const findNearestRiver = async (lng, lat, thresholdDeg = 0.15) => {
  const rivers = await loadEthiopianRivers();
  if (!rivers) return null;

  const point = [lng, lat];
  let nearestRiver = null;
  let nearestDist = thresholdDeg;

  for (const feature of rivers.features) {
    const geom = feature.geometry;
    let dist = Infinity;

    if (geom.type === 'LineString') {
      dist = pointToLineDistance(point, geom.coordinates);
    } else if (geom.type === 'MultiLineString') {
      for (const line of geom.coordinates) {
        const lineDist = pointToLineDistance(point, line);
        if (lineDist < dist) dist = lineDist;
      }
    }

    if (dist < nearestDist) {
      nearestDist = dist;
      nearestRiver = {
        name: feature.properties.name,
        name_original: feature.properties.name_original,
        alt_name: feature.properties.alt_name,
        distance_deg: dist
      };
    }
  }

  return nearestRiver;
};

// Load Ethiopian basins GeoJSON for spatial queries
export const loadEthiopianBasins = async () => {
  if (basinsGeoJSON) return basinsGeoJSON;

  try {
    const response = await fetch('/data/ethiopia_basins.json');
    basinsGeoJSON = await response.json();
    return basinsGeoJSON;
  } catch (e) {
    console.error('Failed to load Ethiopian basins:', e);
    return null;
  }
};

// Point-in-polygon test using ray casting algorithm
const pointInPolygon = (point, polygon) => {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];

    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

    if (intersect) inside = !inside;
  }

  return inside;
};

// Check if point is in a polygon or multipolygon feature
const pointInFeature = (point, feature) => {
  const geom = feature.geometry;

  if (geom.type === 'Polygon') {
    // Check outer ring (first ring)
    return pointInPolygon(point, geom.coordinates[0]);
  } else if (geom.type === 'MultiPolygon') {
    // Check each polygon
    for (const polygon of geom.coordinates) {
      if (pointInPolygon(point, polygon[0])) {
        return true;
      }
    }
  }
  return false;
};

// Find which Ethiopian basin contains a given point
export const findBasinForPoint = async (lng, lat) => {
  const basins = await loadEthiopianBasins();
  if (!basins) return null;

  const point = [lng, lat];

  for (const feature of basins.features) {
    if (pointInFeature(point, feature)) {
      return feature.properties;
    }
  }

  return null;
};

// Get basin intelligence data based on basin name
export const getBasinIntelligence = (basinName) => {
  const BASIN_INTEL = {
    "Abbay (Blue Nile)": {
      name: "Abbay (Blue Nile) Basin",
      destination: "Mediterranean Sea via Nile",
      role: "Major Nile tributary, 85% of Nile water",
      insight: "Ethiopia's largest basin. GERD dam strategic asset. Controls water to Egypt & Sudan."
    },
    "Tekeze": {
      name: "Tekeze Basin",
      destination: "Nile via Atbara River",
      role: "Northern Nile tributary",
      insight: "Strategic northern corridor. Tekeze Dam provides power to Tigray region."
    },
    "Awash": {
      name: "Awash Basin",
      destination: "Danakil Depression (endorheic)",
      role: "Internal drainage, agricultural heartland",
      insight: "Most developed basin. Feeds industrial corridor to Djibouti."
    },
    "Wabi Shebelle": {
      name: "Wabi Shebelle Basin",
      destination: "Somalia (seasonal to Indian Ocean)",
      role: "Trans-boundary eastern drainage",
      insight: "Key to Somali Region water security. Shared resource tension with Somalia."
    },
    "Genale-Dawa": {
      name: "Genale-Dawa Basin",
      destination: "Juba River to Indian Ocean",
      role: "Southern trans-boundary drainage",
      insight: "Hydropower potential. Genale-Dawa III dam under development."
    },
    "Omo-Gibe": {
      name: "Omo-Gibe Basin",
      destination: "Lake Turkana (Kenya)",
      role: "Cross-border southern drainage",
      insight: "Gibe cascade dams. Controversy over Lake Turkana water levels."
    },
    "Baro-Akobo": {
      name: "Baro-Akobo Basin",
      destination: "White Nile via Sobat",
      role: "Western Nile tributary",
      insight: "Untapped hydropower. Key corridor to South Sudan."
    },
    "Rift Valley Lakes": {
      name: "Rift Valley Lakes Basin",
      destination: "Closed basin (endorheic)",
      role: "Internal drainage chain",
      insight: "Tourist and ecological corridor. Lake Ziway, Langano, Abijata, Shala."
    },
    "Danakil (Afar)": {
      name: "Danakil Basin",
      destination: "Danakil Depression",
      role: "Afar triangle drainage",
      insight: "Extreme geology. Potash mining potential. Lowest point in Africa."
    },
    "Mereb-Gash": {
      name: "Mereb-Gash Basin",
      destination: "Eritrea (to Red Sea coast)",
      role: "Northern trans-boundary",
      insight: "Shared with Eritrea. Border demarcation significance."
    },
    "Ethiopian Highlands": {
      name: "Ethiopian Highlands",
      destination: "Various regional drainage",
      role: "Highland water tower",
      insight: "Source region for multiple major rivers. High rainfall capture zone."
    }
  };

  return BASIN_INTEL[basinName] || {
    name: basinName || "Unknown Basin",
    destination: "Unknown",
    role: "Unclassified drainage",
    insight: "Basin data not available for this region."
  };
};

export const addRivers = ({
  map,
  featureData,
  lineColor = "#38B6FF",
  lineWidth = 1,
  sourceID = "route",
}) => {
  const features = featureData.map((river) => {
    // Some rivers in some files have multiple linestrings (such as the Mississippi)...
    // their coordinates will be a triple-nested array instead of a double-nested
    const featureType = Array.isArray(river.geometry.coordinates[0][0])
      ? "MultiLineString"
      : "LineString";

    return {
      type: "Feature",
      properties: river.properties,
      geometry: {
        type: featureType,
        coordinates: river.geometry.coordinates,
      },
    };
  });

  map.addSource(sourceID, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features,
    },
  });

  map.addLayer({
    id: sourceID,
    type: "line",
    source: sourceID,
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": lineColor,
      "line-width": lineWidth,
    },
  });
};
